import {Preview} from 'platform/components';
import {
  Box,
  HStack,
  Integer,
  makeStorageUrl,
  Show,
  StorageImage,
  ThemeIconKey,
} from 'platform/foundation';

import {round} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {IconWithActions} from './IconWithActions';

export interface EntityHeaderImageProps extends TestIdProps {
  image?: string;
  onImageClick?: () => void;
  icon?: ThemeIconKey;
  ratio?: `${number}/${number}`;
}

export function EntityHeaderImage(props: EntityHeaderImageProps) {
  const RATIO = props.ratio ? parseRatio(props.ratio) : 4 / 3;
  const HEIGHT_IN_STEPS: Integer = 30;

  const WIDTH_IN_STEPS = (HEIGHT_IN_STEPS * RATIO) as Integer;
  const ICON_SIZE = round(HEIGHT_IN_STEPS / 2) as Integer;

  if (!props.icon && !props.image) {
    return null;
  }

  return (
    <Box width={WIDTH_IN_STEPS} height={HEIGHT_IN_STEPS}>
      <Box overflow="hidden" borderRadius="medium" backgroundColor="palettes.neutral.30.100">
        <Show when={props.image}>
          <Show when={props.onImageClick}>
            <Preview
              url={props.image ?? ''}
              makeUrl={makeStorageUrl}
              width={WIDTH_IN_STEPS}
              height={HEIGHT_IN_STEPS}
              fit="cover"
              onClick={props.onImageClick!}
              data-testid={suffixTestId('entityHeaderImage-image', props)}
            />
          </Show>
          <Show when={!props.onImageClick}>
            <StorageImage
              url={props.image}
              width={WIDTH_IN_STEPS}
              height={HEIGHT_IN_STEPS}
              fit="cover"
              data-testid={suffixTestId('entityHeaderImage-image', props)}
            />
          </Show>
        </Show>
        <Show when={props.icon && !props.image}>
          <HStack width="100%" height={HEIGHT_IN_STEPS} align="center" justify="center">
            <IconWithActions
              width={WIDTH_IN_STEPS}
              height={HEIGHT_IN_STEPS}
              icon={props.icon}
              iconSize={ICON_SIZE}
              onClick={props.onImageClick}
              data-testid={suffixTestId('entityHeaderImage-icon', props)}
            />
          </HStack>
        </Show>
      </Box>
    </Box>
  );
}

const parseRatio = (ratio: `${number}/${number}`) => {
  const [w, h] = ratio.split('/').map(Number);
  const isValid = Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0;

  if (!isValid) {
    return 4 / 3;
  }

  return w / h;
};
